import axios from 'axios';
import type {
  MembersListResponse,
  MyWorkspacesResponse,
  InviteMemberDto,
  UpdateMemberRoleDto,
  TransferOwnershipDto,
  SetDefaultWorkspaceDto,
  CreateWorkspaceDto,
  InvitationDetailsResponse,
  WorkspaceRole,
} from '@optidev/shared-types';
import { auth } from '@lib/firebase';
import { getActiveWorkspaceId } from '@/stores/workspace-store';

const API_BASE = `${import.meta.env.VITE_API_URL}/api`;

// Create axios instance with auth + workspace interceptor
const apiClient = axios.create();

apiClient.interceptors.request.use(async (config) => {
  const token = await auth.getIdToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Add workspace context from store
  const workspaceId = getActiveWorkspaceId();
  if (workspaceId) {
    config.headers['X-Workspace-Id'] = workspaceId;
  }
  return config;
});

/**
 * Workspace Members API Client
 */
export const workspaceMembersApi = {
  // ===========================================================================
  // Member Queries
  // ===========================================================================

  /**
   * Get all members and pending invitations for current workspace
   */
  async getMembers(): Promise<MembersListResponse> {
    const response = await apiClient.get<MembersListResponse>(
      `${API_BASE}/workspace-members`,
    );
    return response.data;
  },

  /**
   * Get all workspaces the current user belongs to.
   * For platform admins, includes current non-member workspace (via X-Workspace-Id header).
   */
  async getMyWorkspaces(): Promise<MyWorkspacesResponse> {
    const response = await apiClient.get<MyWorkspacesResponse>(
      `${API_BASE}/workspace-members/my-workspaces`,
    );
    return response.data;
  },

  // ===========================================================================
  // Invitation Flow
  // ===========================================================================

  /**
   * Invite a new member to workspace (or add existing user directly)
   */
  async inviteMember(
    email: string,
    role: WorkspaceRole,
  ): Promise<{
    type: 'invited' | 'added';
    invitationId?: string;
    memberId?: string;
    email?: string;
    expiresAt?: string;
    user?: { id: string; email: string; displayName: string | null };
  }> {
    const response = await apiClient.post(`${API_BASE}/workspace-members/invite`, {
      email,
      role,
    } as InviteMemberDto);
    return response.data;
  },

  /**
   * Get invitation details by token (public - for landing page)
   */
  async getInvitation(token: string): Promise<InvitationDetailsResponse> {
    const response = await apiClient.get<InvitationDetailsResponse>(
      `${API_BASE}/invitations/${token}`,
    );
    return response.data;
  },

  /**
   * Accept an invitation
   */
  async acceptInvitation(
    token: string,
  ): Promise<{ success: boolean; type: string; targetId: string }> {
    const response = await apiClient.post(
      `${API_BASE}/invitations/${token}/accept`,
    );
    return response.data;
  },

  // ===========================================================================
  // Member Management
  // ===========================================================================

  /**
   * Update a member's role
   */
  async updateMemberRole(
    memberId: string,
    role: WorkspaceRole,
  ): Promise<{ success: boolean; newRole: WorkspaceRole }> {
    const response = await apiClient.put(
      `${API_BASE}/workspace-members/${memberId}/role`,
      { role } as UpdateMemberRoleDto,
    );
    return response.data;
  },

  /**
   * Remove a member from workspace
   */
  async removeMember(memberId: string): Promise<{ success: boolean }> {
    const response = await apiClient.delete(
      `${API_BASE}/workspace-members/${memberId}`,
    );
    return response.data;
  },

  /**
   * Leave the current workspace
   */
  async leaveWorkspace(): Promise<{ success: boolean }> {
    const response = await apiClient.post(`${API_BASE}/workspace-members/leave`);
    return response.data;
  },

  // ===========================================================================
  // Ownership & Workspace Management
  // ===========================================================================

  /**
   * Transfer workspace ownership to another admin
   */
  async transferOwnership(
    newOwnerId: string,
  ): Promise<{ success: boolean; newOwnerId: string }> {
    const response = await apiClient.put(
      `${API_BASE}/workspace-members/transfer-ownership`,
      { newOwnerId } as TransferOwnershipDto,
    );
    return response.data;
  },

  /**
   * Create a new workspace (free plan, first workspace only).
   */
  async createWorkspace(
    name: string,
    industry?: string,
  ): Promise<{
    workspace: { id: string; refId: string; name: string };
    membership: { id: string; role: string };
  }> {
    const response = await apiClient.post(
      `${API_BASE}/workspace-members/create-workspace`,
      { name, industry } as CreateWorkspaceDto,
    );
    return response.data;
  },

  /**
   * Create a new workspace with a paid plan (2nd+ workspace).
   * Returns a Stripe Checkout URL — workspace is created after payment.
   */
  async createWorkspaceWithPlan(
    name: string,
    planType: 'standard' | 'pro',
    interval: 'month' | 'year',
  ): Promise<{ url: string }> {
    const response = await apiClient.post(
      `${API_BASE}/billing/create-workspace-with-plan`,
      { name, planType, interval },
    );
    return response.data;
  },

  /**
   * Set user's default workspace
   */
  async setDefaultWorkspace(
    workspaceId: string,
  ): Promise<{ success: boolean; defaultWorkspaceId: string }> {
    const response = await apiClient.put(
      `${API_BASE}/workspace-members/default-workspace`,
      { workspaceId } as SetDefaultWorkspaceDto,
    );
    return response.data;
  },

  // ===========================================================================
  // Invitation Management
  // ===========================================================================

  /**
   * Revoke a pending invitation
   */
  async revokeInvitation(invitationId: string): Promise<{ success: boolean }> {
    const response = await apiClient.delete(
      `${API_BASE}/workspace-members/invitations/${invitationId}`,
    );
    return response.data;
  },

  /**
   * Resend invitation email
   */
  async resendInvitation(
    invitationId: string,
  ): Promise<{ success: boolean; newExpiresAt: string }> {
    const response = await apiClient.post(
      `${API_BASE}/workspace-members/invitations/${invitationId}/resend`,
    );
    return response.data;
  },
};

export default workspaceMembersApi;
